import { ApolloLink, Observable } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  createGraphqlNetworkObserver,
  type GraphqlNetworkObservation,
} from "@ths/shared/observability";

import { reportGraphqlNetworkFailure } from "./sentry";

export function createGraphqlNetworkObservabilityLinks(): {
  observabilityLink: ApolloLink;
  attemptLink: ApolloLink;
} {
  const networkObserver = createGraphqlNetworkObserver({
    reportFailure: reportGraphqlNetworkFailure,
    getRoute: () => window.location.pathname,
    getOnline: () => navigator.onLine,
  });

  const observabilityLink = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const observation = networkObserver.begin({
      operationName: operation.operationName || "anonymous",
      operationType:
        definition.kind === "OperationDefinition"
          ? definition.operation
          : "unknown",
    });
    operation.setContext(({ headers = {} }) => ({
      networkObservation: observation,
      headers: {
        ...headers,
        "x-client-request-id": observation.clientRequestId,
      },
    }));

    return new Observable((sink) => {
      const subscription = forward(operation).subscribe({
        next: (result) => sink.next(result),
        complete: () => sink.complete(),
        error: (error) => {
          observation.reportError(error);
          sink.error(error);
        },
      });
      return () => subscription.unsubscribe();
    });
  });

  const attemptLink = new ApolloLink((operation, forward) => {
    const observation = operation.getContext()
      .networkObservation as GraphqlNetworkObservation;
    observation?.recordAttempt();
    return forward(operation);
  });

  return { observabilityLink, attemptLink };
}
